import type { ComputedRef, Ref } from 'vue'
import { computed } from 'vue'
import type { ButtonProps } from '@/components/ui/buttons/useThButton'
import { linkTargetToAnchorTarget } from '@/helpers/api'
import { assertNever } from '@/helpers'
import type {
    ButtonSize as ApiButtonSize,
    ButtonType as ApiButtonType,
    Color,
    EnquiryImageInput,
    EnquiryType,
    LinkFragment,
    LinkItemFragment,
    Maybe,
} from '@/graphql/api'
import type {
    ButtonColors,
    ButtonSize,
    ButtonType,
} from '@/components/ui/buttons'
import { useEnquiryFormStore } from '@/stores/enquiryForm'
import { storeToRefs } from 'pinia'
import { useContactUs } from '@/modules/contact-us'
import { type NavigationFailure, useRouter } from 'vue-router'
import { useSpeakToExpertModal } from '@/composables/app/useSpeakToExpertModal'
import { useRoutes } from '@/router/routes'
import { useRouterNavigation } from '@/plugins/released-urls/useRouterNavigation'

import { useIntercomLauncher } from '@/plugins/intercom/launcher.ts'

export function useCmsLinkItemAction() {
    const routes = useRoutes()
    const { routerPush } = useRouterNavigation()
    const { openEnquiryForm } = useEnquiryFormStore()
    const { open: openContactUs } = useContactUs()
    const { open: openSpeakToExpertModal } = useSpeakToExpertModal()
    const { show: showIntercom } = useIntercomLauncher()

    return async (
        linkItem: LinkItemFragment
    ): Promise<void | NavigationFailure | undefined> => {
        switch (linkItem.__typename) {
            case 'LinkItemCustom':
                return await routerPush(linkItem.url)
            case 'LinkItemAffirm':
                return undefined
            case 'LinkItemBookingForm':
                return await routerPush(routes.booking())
            case 'Post':
                return await routerPush(routes.blog.post(linkItem))
            case 'Page':
                return await routerPush(routes.page(linkItem.path))
            case 'Questionnaire':
                return await routerPush(routes.questionnaire(linkItem))
            case 'LinkItemOpenSpeakToExpertModal':
                openSpeakToExpertModal()
                break
            case 'LinkItemOpenContactForm':
                openContactUs()
                break
            case 'LinkItemOpenIntercom':
                await showIntercom()
                break
            case 'LinkItemOpenEnquiryForm':
                openEnquiryForm(linkItem.enquiryType)
                break
            default:
                return assertNever(linkItem, undefined)
        }
    }
}

type CmsLinkPrerequisites = {
    openEnquiryForm: (type: EnquiryType, image?: EnquiryImageInput) => void
    isLoadingEnquiry: Ref<boolean>
    enquiryType: Ref<Maybe<EnquiryType>>
    openContactUs: () => void
    openSpeakToExpertModal: () => void
    showIntercom: () => Promise<void>
    router: ReturnType<typeof useRouter>
    routes: ReturnType<typeof useRoutes>
}

export function useCmsLinkPrerequisites(): CmsLinkPrerequisites {
    const enquiryFormStore = useEnquiryFormStore()
    const { openEnquiryForm } = useEnquiryFormStore()
    const { enquiryType, isFetching: isLoadingEnquiry } =
        storeToRefs(enquiryFormStore)
    const { open: openContactUs } = useContactUs()
    const { open: openSpeakToExpertModal } = useSpeakToExpertModal()
    const { show: showIntercom } = useIntercomLauncher()
    const router = useRouter()
    const routes = useRoutes()

    return {
        openEnquiryForm,
        isLoadingEnquiry,
        enquiryType,
        openContactUs,
        openSpeakToExpertModal,
        showIntercom,
        router,
        routes,
    }
}

export function createCmsLink(
    link: Readonly<Maybe<LinkFragment>>,
    prerequisites: CmsLinkPrerequisites
): ButtonProps {
    const {
        openEnquiryForm,
        isLoadingEnquiry,
        enquiryType,
        openContactUs,
        openSpeakToExpertModal,
        showIntercom,
        router,
        routes,
    } = prerequisites

    if (!link) {
        return {}
    }

    const linkItem = link.item
    const target = linkTargetToAnchorTarget(link.target)

    // If loading on all buttons that have this enquiry type is undesirable, use this
    // const loading = ref(false)
    //
    // watch(isFetching, (n) => {
    //     if (!n) {
    //         loading.value = false
    //     }
    // })

    switch (linkItem.__typename) {
        case 'LinkItemCustom':
            return {
                href: linkItem.url,
                target,
            }
        case 'Page':
            return {
                // This uses TO in order to render a RouterLink
                to: routes.page(linkItem.path),
                target,
            }
        case 'Post':
            return {
                to: routes.blog.post(linkItem),
                target,
            }
        case 'Questionnaire':
            return {
                to: routes.questionnaire(linkItem, {
                    source: router.currentRoute.value.path,
                }),
            }
        case 'LinkItemOpenEnquiryForm':
            return {
                clicked: (e: PointerEvent) => {
                    openEnquiryForm(linkItem.enquiryType)
                    e.preventDefault()
                    e.stopPropagation()
                },
                loading:
                    isLoadingEnquiry.value &&
                    enquiryType.value === linkItem.enquiryType,
            }
        case 'LinkItemOpenContactForm':
            return {
                clicked: (e: PointerEvent) => {
                    openContactUs()
                    e.preventDefault()
                    e.stopPropagation()
                },
            }
        case 'LinkItemOpenSpeakToExpertModal':
            return {
                clicked: (e: PointerEvent) => {
                    openSpeakToExpertModal()
                    e.preventDefault()
                    e.stopPropagation()
                },
            }
        case 'LinkItemBookingForm':
            return {
                to: routes.booking(),
                target,
            }
        case 'LinkItemOpenIntercom':
            return {
                clicked: async (e: PointerEvent) => {
                    await showIntercom()
                    e.preventDefault()
                    e.stopPropagation()
                },
            }
        case 'LinkItemAffirm':
            // handled outside
            return {}
        default:
            return assertNever(linkItem, {})
    }
}

export function useCmsLink(
    link: Readonly<Ref<Maybe<LinkFragment>>>
): ComputedRef<ButtonProps> {
    const prerequisites = useCmsLinkPrerequisites()

    return computed(() => createCmsLink(link.value, prerequisites))
}

export const CmsColorToButtonColors: Record<Color, ButtonColors> = {
    SLATE: 'var(--slate)',
    GREEN: 'var(--green)',
    TAAFFEITE: 'var(--taaffeite)',
    BLUE: 'var(--blue)',
    RED: 'var(--red)',
    SAGE: 'var(--sage)',
    BLUSH: 'var(--blush)',
    STONE: 'var(--stone)',
    STONE2: 'var(--stone-2)',
    STONE3: 'var(--stone-3)',
    WHITE: 'var(--white)',
}

export const cmsColorToButtonColor = (color: Color): ButtonColors =>
    CmsColorToButtonColors[color] || 'var(--blue)'

export const CmsToButtonSizes: {
    [K in ApiButtonSize]: ButtonSize
} = {
    MEDIUM: 'medium',
    SMALL: 'small',
}

export const CmsToButtonTypes: {
    [K in ApiButtonType]: ButtonType
} = Object.freeze({
    SOLID: 'solid',
    BORDERED: 'bordered',
    LINK: 'link',
})
